/**
 * OneSignal Push Notification Service
 * Handles SDK initialization and push notification management
 */

import OneSignal from 'react-onesignal';
import { isNativeApp, getPlatform } from '@/utils/platform';

// Storage keys for tracking OneSignal state
const ONESIGNAL_INITIALIZED_KEY = 'evolve-onesignal-initialized';
const ONESIGNAL_USER_ID_KEY = 'evolve-onesignal-user-id';

let isInitialized = false;

/**
 * Initialize OneSignal SDK
 * Should be called once when the app starts
 */
export const initializeOneSignal = async (appId: string): Promise<boolean> => {
  if (isInitialized) {
    console.log('[OneSignal] Already initialized');
    return true;
  }

  if (!appId) {
    console.warn('[OneSignal] No App ID provided, skipping initialization');
    return false;
  }

  try {
    const platform = getPlatform();
    console.log(`[OneSignal] Initializing on platform: ${platform}`);

    // For web, use the react-onesignal SDK
    if (!isNativeApp()) {
      await OneSignal.init({
        appId,
        allowLocalhostAsSecureOrigin: import.meta.env.DEV,
      });

      isInitialized = true;
      localStorage.setItem(ONESIGNAL_INITIALIZED_KEY, 'true');
      console.log('[OneSignal] Web SDK initialized successfully');
      return true;
    } else {
      // For native apps, OneSignal Capacitor plugin handles initialization
      // This is configured in the native project's capacitor.config.ts
      console.log('[OneSignal] Native app detected - using Capacitor plugin');
      isInitialized = true;
      return true;
    }
  } catch (error) {
    console.error('[OneSignal] Initialization error:', error);
    return false;
  }
};

/**
 * Check if OneSignal is initialized
 */
export const isOneSignalInitialized = (): boolean => {
  return isInitialized;
};

/**
 * Request notification permission via OneSignal
 */
export const requestPushPermission = async (): Promise<boolean> => {
  if (!isInitialized) {
    console.warn('[OneSignal] Not initialized, cannot request permission');
    return false;
  }

  try {
    if (!isNativeApp()) {
      // Web: Use OneSignal's slidedown or native prompt
      await OneSignal.Slidedown.promptPush();
      
      // Check if permission was granted
      const permission = await OneSignal.Notifications.permission;
      return permission;
    } else {
      // Native: Permission is typically handled at app install or via native prompts
      console.log('[OneSignal] Native permission request');
      return true;
    }
  } catch (error) {
    console.error('[OneSignal] Permission request error:', error);
    return false;
  }
};

/**
 * Set the external user ID to link OneSignal with your user system
 * This allows targeted push notifications to specific users
 */
export const setExternalUserId = async (userId: string): Promise<void> => {
  if (!isInitialized) {
    console.warn('[OneSignal] Not initialized, cannot set external user ID');
    return;
  }

  try {
    // Clear cached user ID to force refresh (important when switching from UUID to email)
    localStorage.removeItem(ONESIGNAL_USER_ID_KEY);

    if (!isNativeApp()) {
      await OneSignal.login(userId);
    }
    
    localStorage.setItem(ONESIGNAL_USER_ID_KEY, userId);
    console.log('[OneSignal] External user ID set:', userId);
  } catch (error) {
    console.error('[OneSignal] Error setting external user ID:', error);
  }
};

/**
 * Remove the external user ID (on logout)
 */
export const removeExternalUserId = async (): Promise<void> => {
  if (!isInitialized) return;

  try {
    if (!isNativeApp()) {
      await OneSignal.logout();
    }
    
    localStorage.removeItem(ONESIGNAL_USER_ID_KEY);
    console.log('[OneSignal] External user ID removed');
  } catch (error) {
    console.error('[OneSignal] Error removing external user ID:', error);
  }
};

/**
 * Send a tag to OneSignal for user segmentation
 * Tags can be used to target specific user groups
 */
export const sendTag = async (key: string, value: string): Promise<void> => {
  if (!isInitialized) return;

  try {
    if (!isNativeApp()) {
      await OneSignal.User.addTag(key, value);
    }
    console.log(`[OneSignal] Tag sent: ${key}=${value}`);
  } catch (error) {
    console.error('[OneSignal] Error sending tag:', error);
  }
};

/**
 * Send multiple tags at once
 */
export const sendTags = async (tags: Record<string, string>): Promise<void> => {
  if (!isInitialized) return;

  try {
    if (!isNativeApp()) {
      await OneSignal.User.addTags(tags);
    }
    console.log('[OneSignal] Tags sent:', tags);
  } catch (error) {
    console.error('[OneSignal] Error sending tags:', error);
  }
};

/**
 * Check if push notifications are enabled
 */
export const isPushEnabled = async (): Promise<boolean> => {
  if (!isInitialized) return false;

  try {
    if (!isNativeApp()) {
      return await OneSignal.Notifications.permission;
    }
    return true; // Native apps handle this differently
  } catch (error) {
    console.error('[OneSignal] Error checking push status:', error);
    return false;
  }
};

/**
 * Get the OneSignal subscription ID (player ID)
 */
export const getSubscriptionId = async (): Promise<string | null> => {
  if (!isInitialized) return null;

  try {
    if (!isNativeApp()) {
      const id = await OneSignal.User.PushSubscription.id;
      return id || null;
    }
    return null;
  } catch (error) {
    console.error('[OneSignal] Error getting subscription ID:', error);
    return null;
  }
};

/**
 * Get detailed subscription status for debugging
 */
export const getSubscriptionStatus = async (): Promise<{
  isSubscribed: boolean;
  subscriptionId: string | null;
  permission: string;
  externalId: string | null;
}> => {
  if (!isInitialized || isNativeApp()) {
    return { 
      isSubscribed: false, 
      subscriptionId: null, 
      permission: 'unknown',
      externalId: null 
    };
  }

  try {
    const subscriptionId = await OneSignal.User.PushSubscription.id;
    const permission = typeof Notification !== 'undefined' ? Notification.permission : 'unknown';
    const externalId = localStorage.getItem(ONESIGNAL_USER_ID_KEY);

    return {
      isSubscribed: !!subscriptionId,
      subscriptionId: subscriptionId || null,
      permission,
      externalId,
    };
  } catch (error) {
    console.error('[OneSignal] Error getting subscription status:', error);
    return { 
      isSubscribed: false, 
      subscriptionId: null, 
      permission: 'unknown',
      externalId: null 
    };
  }
};
